import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { catalogFormatShort } from '@/lib/catalogFormat';

type Props = {
  label: string | undefined | null;
};

/**
 * Shows the full format/type string unless it overflows the format column —
 * then Comp. / Collab. / Rmx. / SP when a short form exists.
 */
export function CatalogFormatLabel({ label }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const full = (label ?? '').trim();
  const short = catalogFormatShort(full);
  const [display, setDisplay] = useState(full);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (!full) {
      setDisplay('');
      return;
    }
    if (!short) {
      setDisplay(full);
      return;
    }
    el.textContent = full;
    const overflows = el.scrollWidth > el.clientWidth + 0.5;
    setDisplay(overflows ? short : full);
  }, [full, short]);

  useLayoutEffect(() => {
    setDisplay(full);
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    // Parent column can settle after fonts / grid — remeasure once more.
    const parent = el.parentElement;
    if (parent) ro.observe(parent);
    return () => ro.disconnect();
  }, [full, measure]);

  return (
    <span ref={ref} className="row-format" title={full || undefined}>
      {display}
    </span>
  );
}
